import { kstDateKey } from "./format";
import {
	budgetPeriodFor,
	type LivingBudgetPlan,
	type RecurringPayment,
	recurringPaymentDate,
	recurringPaymentRecorded,
} from "./household-consumption";

export interface DailyExpense {
	transactedAt: string;
	totalAmount: number;
	deletedAt?: string | null;
	recurringPaymentId?: string | null;
	sensitiveMasked?: boolean;
}
export interface DayClose {
	date: string;
	spent: number;
	allowance: number | null;
	kind: "zero" | "under" | "close_only";
	homeItemIds: string[];
}
export const DAY_CLOSES_KEY = "dayCloses";
const DAY = 86_400_000;
export function dateAnchor(key: string): Date {
	return new Date(`${key}T12:00:00+09:00`);
}
export function validDayKey(value: string): boolean {
	return (
		/^\d{4}-\d{2}-\d{2}$/.test(value) &&
		Number.isFinite(dateAnchor(value).getTime()) &&
		kstDateKey(dateAnchor(value)) === value
	);
}
export function parseDayCloses(raw: string | null): DayClose[] {
	if (raw === null) return [];
	const value: unknown = JSON.parse(raw);
	if (!Array.isArray(value)) throw new Error("마감 기록을 읽지 못했어요.");
	const dates = new Set<string>();
	for (const row of value) {
		if (
			!row ||
			typeof row !== "object" ||
			!validDayKey(row.date) ||
			dates.has(row.date) ||
			!Number.isSafeInteger(row.spent) ||
			row.spent < 0 ||
			(row.allowance !== null &&
				(!Number.isSafeInteger(row.allowance) || row.allowance < 0)) ||
			!["zero", "under", "close_only"].includes(row.kind) ||
			!Array.isArray(row.homeItemIds) ||
			row.homeItemIds.some((id: unknown) => typeof id !== "string") ||
			new Set(row.homeItemIds).size !== row.homeItemIds.length
		)
			throw new Error("마감 기록을 읽지 못했어요.");
		dates.add(row.date);
	}
	return value as DayClose[];
}
/** One saved stamp per date. Counts are derived from expenses, never stamps. */
export function upsertDayClose(
	closes: DayClose[],
	close: DayClose,
): DayClose[] {
	return [...closes.filter((row) => row.date !== close.date), close].sort(
		(a, b) => a.date.localeCompare(b.date),
	);
}

/** All due dates inside the user's period, including payday months spanning two calendar months. */
function scheduledPayments(
	payments: RecurringPayment[],
	start: Date,
	end: Date,
) {
	const dates = new Map<
		string,
		{ payment: RecurringPayment; date: string; anchor: Date }
	>();
	for (let time = start.getTime(); time < end.getTime(); time += DAY) {
		const anchor = new Date(time);
		for (const payment of payments.filter((p) => !p.paused)) {
			const date = recurringPaymentDate(payment, anchor);
			const at = Date.parse(`${date}T00:00:00+09:00`);
			if (at >= start.getTime() && at < end.getTime())
				dates.set(`${payment.id}:${date}`, { payment, date, anchor });
		}
	}
	return [...dates.values()];
}
export function calculateDailyAllowance<T extends DailyExpense>(
	expenses: T[],
	plan: LivingBudgetPlan,
	payments: RecurringPayment[] = [],
	now = new Date(),
) {
	const period = budgetPeriodFor(now, plan.monthStartDay);
	const today = kstDateKey(now);
	const todayStart = Date.parse(`${today}T00:00:00+09:00`);
	const tomorrow = todayStart + DAY;
	const active = expenses.filter(
		(r) =>
			!r.deletedAt &&
			Date.parse(r.transactedAt) >= period.start.getTime() &&
			Date.parse(r.transactedAt) < period.end.getTime(),
	);
	const known = active.filter((r) => Date.parse(r.transactedAt) < tomorrow);
	const schedules = scheduledPayments(payments, period.start, period.end);
	const pending = schedules.filter(
		({ payment, anchor }) =>
			!recurringPaymentRecorded(payment.id, known, anchor),
	);
	const pendingReserved = pending.reduce(
		(sum, { payment }) => sum + payment.amount,
		0,
	);
	const linked = (r: DailyExpense) =>
		Boolean(
			r.recurringPaymentId &&
				schedules.some(
					({ payment, date }) =>
						payment.id === r.recurringPaymentId &&
						date.slice(0, 7) === kstDateKey(r.transactedAt).slice(0, 7),
				),
		);
	// A linked payment replaces its reservation, including on the day it is saved.
	// Counting it again as today's discretionary spend would deduct it twice.
	const recordedReserved = known
		.filter(linked)
		.reduce((sum, r) => sum + r.totalAmount, 0);
	const spentBefore = known
		.filter((r) => !linked(r) && Date.parse(r.transactedAt) < todayStart)
		.reduce((sum, r) => sum + r.totalAmount, 0);
	const todayRecords = known.filter(
		(r) => kstDateKey(r.transactedAt) === today,
	);
	const spent = todayRecords
		.filter((r) => !linked(r))
		.reduce((sum, r) => sum + r.totalAmount, 0);
	const daysLeft = Math.max(
		1,
		Math.round((period.end.getTime() - todayStart) / DAY),
	);
	const spendable =
		Number.isSafeInteger(plan.monthlySpendable) &&
		(plan.monthlySpendable ?? 0) > 0
			? (plan.monthlySpendable ?? 0)
			: null;
	const pool =
		spendable === null
			? null
			: spendable - pendingReserved - recordedReserved - spentBefore;
	const raw = pool === null ? null : Math.max(0, pool) / daysLeft;
	const allowance =
		raw === null
			? null
			: daysLeft === 1 || raw < 1000
				? Math.floor(raw)
				: Math.floor(raw / 1000) * 1000;
	const remaining = allowance === null ? null : Math.max(0, allowance - spent);
	const periodSpent = active.reduce((sum, r) => sum + r.totalAmount, 0);
	const exceeded =
		spendable === null ? 0 : Math.max(0, periodSpent - spendable);
	return {
		today,
		period,
		daysLeft,
		spendable,
		allowance,
		remaining,
		spent,
		spentBefore,
		pendingReserved,
		recordedReserved,
		periodSpent,
		exceeded,
		overToday: allowance === null ? 0 : Math.max(0, spent - allowance),
		todayRecords,
		pending,
	};
}
export function deriveDayKind(
	spent: number,
	allowance: number | null,
): DayClose["kind"] {
	return spent === 0
		? "zero"
		: allowance !== null && spent <= allowance
			? "under"
			: "close_only";
}
/** Days from the first record/close; today joins only once it has a record or an explicit close. */
export function deriveDayDogam(
	expenses: DailyExpense[],
	plan: LivingBudgetPlan,
	payments: RecurringPayment[],
	closes: DayClose[],
	anchor = new Date(),
	now = new Date(),
) {
	const period = budgetPeriodFor(anchor, plan.monthStartDay);
	const today = kstDateKey(now);
	const hasToday =
		expenses.some(
			(r) => !r.deletedAt && kstDateKey(r.transactedAt) === today,
		) || closes.some((c) => c.date === today);
	const end = Math.min(
		period.end.getTime(),
		Date.parse(`${today}T00:00:00+09:00`) + (hasToday ? DAY : 0),
	);
	const starts = [
		...expenses
			.filter((r) => !r.deletedAt)
			.map((r) => Date.parse(`${kstDateKey(r.transactedAt)}T00:00:00+09:00`)),
		...closes.map((c) => Date.parse(`${c.date}T00:00:00+09:00`)),
	].filter((t) => t >= period.start.getTime() && t < period.end.getTime());
	const start = starts.length ? Math.min(...starts) : end;
	const days = [];
	for (let time = start; time < end; time += DAY) {
		const daily = calculateDailyAllowance(
			expenses,
			plan,
			payments,
			new Date(time),
		);
		const close = closes.find((c) => c.date === daily.today);
		days.push({
			date: daily.today,
			spent: daily.spent,
			allowance: daily.allowance,
			kind: deriveDayKind(daily.spent, daily.allowance),
			close: close ?? null,
		});
	}
	return {
		period,
		days,
		zeroDays: days.filter((d) => d.kind === "zero").length,
		underDays: days.filter((d) => d.kind !== "close_only").length,
		homeDays: days.filter((d) => (d.close?.homeItemIds.length ?? 0) > 0).length,
	};
}
